import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDPageContainer } from '../primitives/CDPageContainer';
import { CDConfirmReceipt, type ConfirmReceiptData } from '../sections/CDConfirmReceipt';
import {
  CDContactInformation,
  type ContactInformationData,
} from '../sections/CDContactInformation';
import { CDLoanCalculations, type LoanCalculationsData } from '../sections/CDLoanCalculations';
import { CDOtherDisclosures, type OtherDisclosuresData } from '../sections/CDOtherDisclosures';

export interface CDPage5Data {
  loanCalculations: Partial<LoanCalculationsData>;
  otherDisclosures: Partial<OtherDisclosuresData>;
  contactInformation: Partial<ContactInformationData>;
  confirmReceipt: Partial<ConfirmReceiptData>;
}

export interface CDPage5Props {
  /** Page 5 data */
  data: Partial<CDPage5Data>;
  /** Total number of pages */
  totalPages?: number;
  /** Form ID for footer */
  formId?: string;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler for data updates */
  onChange?: (data: Partial<CDPage5Data>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDPage5 - Complete Page 5 of Closing Disclosure (Final Page)
 *
 * Contains:
 * - Loan Calculations (Total of Payments, Finance Charge, APR, TIP)
 * - Other Disclosures
 * - Contact Information table
 * - Confirm Receipt signature section
 * - Page footer
 */
export const CDPage5 = React.forwardRef<HTMLDivElement, CDPage5Props>(
  ({ data, totalPages = 5, formId, editable = false, onChange, className }, ref) => {
    const handleLoanCalculationsChange = (loanCalculations: Partial<LoanCalculationsData>) => {
      onChange?.({ ...data, loanCalculations });
    };

    const handleOtherDisclosuresChange = (otherDisclosures: Partial<OtherDisclosuresData>) => {
      onChange?.({ ...data, otherDisclosures });
    };

    const handleContactInformationChange = (
      contactInformation: Partial<ContactInformationData>,
    ) => {
      onChange?.({ ...data, contactInformation });
    };

    const handleConfirmReceiptChange = (confirmReceipt: Partial<ConfirmReceiptData>) => {
      onChange?.({ ...data, confirmReceipt });
    };

    return (
      <CDPageContainer
        ref={ref}
        pageNumber={5}
        totalPages={totalPages}
        formId={formId}
        className={cn(className)}
      >
        {/* Loan Calculations */}
        <CDLoanCalculations
          data={data.loanCalculations || {}}
          editable={editable}
          onChange={handleLoanCalculationsChange}
        />

        {/* Other Disclosures */}
        <div className="mt-2">
          <CDOtherDisclosures
            data={data.otherDisclosures || {}}
            editable={editable}
            onChange={handleOtherDisclosuresChange}
          />
        </div>

        {/* Contact Information */}
        <div className="mt-2">
          <CDContactInformation
            data={data.contactInformation || {}}
            editable={editable}
            onChange={handleContactInformationChange}
          />
        </div>

        {/* Confirm Receipt */}
        <div className="mt-2">
          <CDConfirmReceipt
            data={data.confirmReceipt || {}}
            editable={editable}
            onChange={handleConfirmReceiptChange}
          />
        </div>
      </CDPageContainer>
    );
  },
);

CDPage5.displayName = 'CDPage5';
